import { Injectable } from '@angular/core';
import * as MicrosoftTeams from '@microsoft/teams-js';
import { Observable, ReplaySubject, Subject, of } from 'rxjs';
import { first } from 'rxjs/operators';
import { duration } from 'moment';
import { Settings } from '@nimbus-global/features/shared/models/settings.model';
import { BaseTeamsService } from '@nimbus-library/features/shared/services/team/base/base-teams.service';
import { CacheService, IEntityCacheService } from '@nimbus-library/features/cache/index';
import { TeamsOperationStatus } from '@nimbus-global/features/shared/models/api/teams-operation-status.model';
import { TeamsContextResolver } from '@nimbus-library/features/microsoft-teams/common/context-resolvers/teams-context.resolver';
import { ITeamsContext } from '@nimbus-library/features/microsoft-teams/models/i-teams.context';

@Injectable()
export class MicrosoftTeamsService extends BaseTeamsService {
  public constructor(
    cacheService: CacheService,
    private readonly teamsContextResolver: TeamsContextResolver,
  ) {
    super();
    this.tabInstacesCache = cacheService.for<MicrosoftTeams.TabInstance[]>('TabInstances', { preloadMaxAge: duration(1, 'month') });
    MicrosoftTeams.app
      .initialize()
      .then(() => {
        this.refreshSettings();
      })
      .catch((error) => {
        console.log("Cannot initialize Microsoft Teams. It's possible Nimbus is not running in teams.", error);
      });
  }
  private readonly nativeSettingsInfo: ReplaySubject<Settings> = new ReplaySubject<Settings>(1);
  private readonly operationResultReceiver: Subject<TeamsOperationStatus> = new Subject();
  private tabInstacesCache: IEntityCacheService<MicrosoftTeams.TabInstance[]>;

  public refreshSettings() {
    MicrosoftTeams.pages.getConfig().then((config) => {
      this.nativeSettingsInfo.next(config as Settings);
    });
  }

  public getSettings$(): Observable<Settings> {
    return this.nativeSettingsInfo.asObservable();
  }

  public setValidityOfSettings(valid: boolean) {
    MicrosoftTeams.settings.setValidityState(valid);
  }

  public publishOperationResult(status: TeamsOperationStatus) {
    this.operationResultReceiver.next(status);
  }

  public getTabInstances(preload: boolean): Observable<MicrosoftTeams.TabInstance[]> {
    if (this.teamsContextResolver.isStandAlone) {
      const cachedInstances = this.tabInstacesCache.get(null);
      return of(cachedInstances || []);
    }

    const loadInstances$ = new Observable<MicrosoftTeams.TabInstance[]>((observer) => {
      MicrosoftTeams.getTabInstances(
        (tabInfo) => {
          observer.next(tabInfo.teamTabs);
          observer.complete();
        },
        {
          favoriteChannelsOnly: false,
          favoriteTeamsOnly: false,
        },
      );
    });

    return loadInstances$.pipe(this.tabInstacesCache.useCache(null, preload));
  }

  private composeWebSiteURL(context: ITeamsContext): string {
    return context && context.teamName && context.groupId
      ? `https://${window.location.hostname}/live-view?groupId=${encodeURI(context.groupId)}&teamName=${encodeURI(context.teamName)}`
      : `https://${window.location.hostname}/personal/overview`;
  }

  public registerOnRemoveHanddler(): void {
    MicrosoftTeams.app.initialize().then(() => {
      MicrosoftTeams.pages.config.registerOnRemoveHandler(async (removeEvent: MicrosoftTeams.pages.config.RemoveEvent) => {
        const promise = this.operationResultReceiver.pipe(first()).toPromise();
        this.teamRemoving.next(removeEvent);
        const result = await promise;

        if (result.success) {
          removeEvent.notifySuccess();
        } else {
          removeEvent.notifyFailure(result.message);
        }
      });

      this.refreshSettings();
    });
  }

  public registerOnSaveHandler(): void {
    MicrosoftTeams.app.initialize().then(() => {
      MicrosoftTeams.pages.config.registerOnSaveHandler(async (saveEvent: MicrosoftTeams.pages.config.SaveEvent) => {
        MicrosoftTeams.settings.setSettings({
          entityId: 'team',
          contentUrl: `https://${window.location.hostname}/live-view`,
          suggestedDisplayName: 'Nimbus',
          websiteUrl: this.composeWebSiteURL(this.teamsContextResolver.context),
          removeUrl: `https://${window.location.hostname}/provisioning/suspend`,
        });
        const promise = this.operationResultReceiver.pipe(first()).toPromise();
        this.onUserSave.next(saveEvent);
        const result = await promise;

        if (result.success) {
          saveEvent.notifySuccess();
        } else {
          saveEvent.notifyFailure(result.message);
        }
      });

      this.refreshSettings();
    });
  }
}
